import { useSyncExternalStore } from "react";
import { z } from "zod";

import { COOKIE_CONSENT_SCHEMA_VERSION, COOKIE_CONSENT_STORAGE_KEY } from "./cookie-consent-storage";

export { COOKIE_CONSENT_STORAGE_KEY };

const SCHEMA_VERSION = COOKIE_CONSENT_SCHEMA_VERSION;

/**
 * Whether the visitor accepted cookies and monitoring — a yes or a no, and nothing finer.
 *
 * The single purpose is a boolean signal to switch the Microsoft Clarity on. There are no
 * categories and no preference centre on purpose; the text that explains what is monitored is
 * the Privacy Policy's section 06, which the banner links to rather than repeats.
 *
 * Built on the same device as `host-preferences.ts`: `localStorage` + Zod + `useSyncExternalStore`.
 * `localStorage` because the answer must outlive closing the browser; a cookie would ride on
 * every request for something the server never reads.
 */

/**
 * Three states, not two. No record is *pending*, and pending is not a refusal: it is what makes
 * the banner appear. A `boolean | null` would let the two be confused at a call site.
 */
export type CookieConsent = "pendente" | "aceito" | "recusado";

/**
 * Every field but the version optional, and parsed with `safeParse`: this is hand-editable
 * storage, so a spoiled record is an unanswered question, not an error.
 */
const consentRecordSchema = z.object({
  v: z.literal(SCHEMA_VERSION),
  accepted: z.boolean().optional(),
  decidedAt: z.string().optional(),
});

function storage(): Storage | null {
  // Absent on the server, and throws on access in privacy-restricted contexts, where merely
  // reading `window.localStorage` is what raises — hence the guard around the access itself.
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readCookieConsent(): CookieConsent {
  let raw: string | null;
  try {
    raw = storage()?.getItem(COOKIE_CONSENT_STORAGE_KEY) ?? null;
  } catch {
    return "pendente";
  }

  if (raw === null) return "pendente";

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return "pendente";
  }

  const record = consentRecordSchema.safeParse(parsed);
  if (!record.success || record.data.accepted === undefined) return "pendente";

  return record.data.accepted ? "aceito" : "recusado";
}

/**
 * Listeners in this tab. The `storage` event only fires in the *other* tabs, so a write has to
 * announce itself here as well or the screen that made it would not repaint.
 */
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

function writeDecision(accepted: boolean): void {
  try {
    storage()?.setItem(
      COOKIE_CONSENT_STORAGE_KEY,
      // ISO, because consent is a datable fact.
      JSON.stringify({ v: SCHEMA_VERSION, accepted, decidedAt: new Date().toISOString() }),
    );
  } catch {
    // A full or refused store must not take the interaction down with it. The choice then lasts
    // for this page only — in a private window, which is the right outcome — because the tab's
    // listeners are told below and the banner still has to go.
  }

  announce();
}

export function acceptCookies(): void {
  writeDecision(true);
}

export function rejectCookies(): void {
  writeDecision(false);
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);

  // Two tabs of the site are one person: answering in one settles the other.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== COOKIE_CONSENT_STORAGE_KEY) return;
    onChange();
  };

  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(onChange);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * The visitor's answer, and the two ways to give it.
 *
 * The server snapshot is `"pendente"`, so the markup React hydrates against is the one the server
 * sent; the stored answer is adopted right after, with no effect that writes state on mount. The
 * snapshot is a string, a primitive, so it cannot loop.
 */
export function useCookieConsent(): {
  consent: CookieConsent;
  accept: () => void;
  reject: () => void;
} {
  const consent = useSyncExternalStore(subscribe, readCookieConsent, () => "pendente" as const);

  return { consent, accept: acceptCookies, reject: rejectCookies };
}
